import React, { useEffect, useRef, useState } from 'react';
import { Play, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIA_ERR = { 1: 'MEDIA_ERR_ABORTED', 2: 'MEDIA_ERR_NETWORK', 3: 'MEDIA_ERR_DECODE', 4: 'MEDIA_ERR_SRC_NOT_SUPPORTED' };

export const ticketResolver = (path, token) => async () => {
  const r = await fetch(`${API_URL}${path}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : `ticket_${r.status}`);
  return `${API_URL}${d.url}`;
};

// Tap-to-load byte-exact player: native controls (seek, audio, fullscreen), on-screen MediaError diagnostics.
export const SecureVideo = ({ resolveSrc, poster, testId, className = '', portrait = false, label = 'Play', tone = 'dark', tracks = [], videoRef = null, posterClassName = 'opacity-40' }) => {
  const [src, setSrc] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [diag, setDiag] = useState(null);
  const ref = useRef(null);
  const resumeAt = useRef(0);
  const retried = useRef(false);

  const load = async () => {
    setPhase('loading'); setDiag(null);
    try { setSrc(await resolveSrc()); setPhase('ready'); }
    catch (e) { setDiag(`Could not open video (${e.message || e})`); setPhase('error'); }
  };

  useEffect(() => {
    if (phase === 'ready' && ref.current) ref.current.play().catch(() => {});
  }, [phase, src]);

  const onError = () => {
    const me = ref.current?.error;
    if (!retried.current) {
      retried.current = true;
      resumeAt.current = ref.current?.currentTime || 0;
      load();
      return;
    }
    setDiag(`${MEDIA_ERR[me?.code] || 'MEDIA_ERR_UNKNOWN'} (code ${me?.code ?? '?'})${me?.message ? ` · ${me.message}` : ''} · network ${ref.current?.networkState ?? '?'}`);
    setPhase('error');
  };

  const box = `relative w-full ${portrait ? 'aspect-[9/16] max-h-[70vh] mx-auto' : 'aspect-video'} overflow-hidden rounded-lg bg-black ${className}`;
  const btnTone = tone === 'light' ? 'bg-white/90 text-slate-900' : 'bg-white/15 text-white border border-white/30';

  return (
    <div className={box} data-testid={testId}>
      {src && (
        <video
          ref={(el) => { ref.current = el; if (videoRef) videoRef.current = el; }}
          src={src}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          controlsList="nodownload"
          className="absolute inset-0 w-full h-full object-contain bg-black"
          onError={onError}
          onPlaying={() => { retried.current = false; }}
          onLoadedMetadata={() => { if (resumeAt.current && ref.current) { ref.current.currentTime = resumeAt.current; resumeAt.current = 0; } }}
          data-testid={`${testId}-video`}
        >
          {tracks.map((t, i) => (
            <track key={t.src} kind="captions" src={t.src} srcLang={t.srclang} label={t.label} default={i === 0} />
          ))}
        </video>
      )}
      {phase !== 'ready' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center bg-gradient-to-br from-slate-900 to-black">
          {poster && <img src={poster} alt="" className={`absolute inset-0 w-full h-full object-cover ${posterClassName}`} data-testid={`${testId}-poster`} />}
          {phase === 'error' ? (
            <>
              <AlertTriangle className="relative w-8 h-8 text-amber-300" />
              <p className="relative text-xs text-amber-100 font-mono break-all max-w-sm" data-testid={`${testId}-error`}>{diag}</p>
              <button onClick={() => { retried.current = false; load(); }} className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${btnTone}`} data-testid={`${testId}-retry`}>
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </>
          ) : (
            <button onClick={load} disabled={phase === 'loading'} className={`relative inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold backdrop-blur-sm transition-transform hover:scale-105 ${btnTone}`} data-testid={`${testId}-load`}>
              {phase === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />} {label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SecureVideo;
